import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Plus, Trash2, Pencil, Download, Briefcase, Inbox, Check,
  X, Sparkles, Search, ArrowLeft, ExternalLink, RotateCcw,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const LOGO = "https://customer-assets-wrfwihn1.emergentagent.net/job_talent-hub-818/artifacts/8a3m5kio_Yellow5_Logo_Layout_Vecto-02.webp";
const SECTORS = ["Pharma & Biotech", "MedTech", "Agroalimentaire", "Aéronautique & Défense", "Chimie & Matériaux", "Industrie & Manufacturing"];
const PROVINCES = ["Anvers", "Brabant flamand", "Brabant wallon", "Flandre occidentale", "Flandre orientale", "Hainaut", "Liège", "Limbourg", "Luxembourg", "Namur", "Bruxelles-Capitale"];
const CONTRACTS = ["CDI", "CDD", "Freelance", "Interim"];

const empty = { title: "", title_en: "", sector: "Pharma & Biotech", location: "", province: "Liège", contract_type: "CDI", description: "", description_en: "", requirements: "", requirements_en: "", is_open: true };
const inputCls = "w-full bg-y5-ink border border-y5-line px-4 py-3 text-white placeholder:text-white/30 focus:border-y5-yellow focus:outline-none transition-colors text-sm";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const isProcessed = (a) => !!a.processed_at;

export default function Admin() {
  const { user, checked, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("todo"); // todo | done | jobs
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [filterOffer, setFilterOffer] = useState("");
  const [filterSector, setFilterSector] = useState("");

  useEffect(() => { if (checked && !user) navigate("/login"); }, [checked, user, navigate]);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => {});
    api.get("/admin/applications").then((r) => setApps(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  // --- Job CRUD ---
  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (job) => {
    setEditing(job.id);
    setForm({ ...empty, ...job, province: job.province || "Liège", requirements: (job.requirements || []).join("\n"), requirements_en: (job.requirements_en || []).join("\n") });
    setDialogOpen(true);
  };
  const saveJob = async (e) => {
    e.preventDefault();
    const payload = { ...form, requirements: form.requirements.split("\n").map((s) => s.trim()).filter(Boolean), requirements_en: (form.requirements_en || "").split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/admin/jobs/${editing}`, payload);
      else await api.post("/admin/jobs", payload);
      toast.success(editing ? "Offre mise à jour" : "Offre publiée");
      setDialogOpen(false);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteJob = async (id) => {
    if (!window.confirm("Supprimer cette offre ?")) return;
    await api.delete(`/admin/jobs/${id}`);
    toast.success("Offre supprimée");
    loadAll();
  };

  // --- Application actions ---
  const markProcessed = async (appId) => {
    await api.post(`/admin/applications/${appId}/process`);
    toast.success("Candidature marquée comme traitée");
    setSelected(null);
    loadAll();
  };
  const markUnprocessed = async (appId) => {
    await api.post(`/admin/applications/${appId}/unprocess`);
    toast.success("Candidature restaurée dans À traiter");
    setSelected(null);
    loadAll();
  };
  const toggleAts = async (appId) => {
    const r = await api.post(`/admin/applications/${appId}/ats`);
    toast.success(r.data.added_to_ats ? "Marquée comme ajoutée à l'ATS" : "Retirée de l'ATS");
    setSelected((s) => (s && s.id === appId ? { ...s, added_to_ats: r.data.added_to_ats } : s));
    loadAll();
  };
  const downloadCV = async (app) => {
    try {
      const res = await api.get(`/admin/applications/${app.id}/cv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = app.cv_filename || "cv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Impossible de télécharger le CV"); }
  };

  // --- Derived lists ---
  const toProcess = useMemo(() => apps.filter((a) => !isProcessed(a)), [apps]);
  const processed = useMemo(() => apps.filter(isProcessed), [apps]);
  const processedFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return processed.filter((a) => {
      if (q && !(a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q))) return false;
      if (filterOffer && (a.job_title || "Candidature spontanée") !== filterOffer) return false;
      if (filterSector && (a.ai?.sector || "") !== filterSector) return false;
      return true;
    });
  }, [processed, query, filterOffer, filterSector]);
  const offerOptions = useMemo(() => Array.from(new Set(processed.map((a) => a.job_title || "Candidature spontanée"))), [processed]);
  const sectorOptions = useMemo(() => Array.from(new Set(processed.map((a) => a.ai?.sector).filter(Boolean))), [processed]);

  if (!user) return <div className="min-h-screen flex items-center justify-center text-y5-muted">Chargement…</div>;

  const navItem = (id, label, icon, count) => (
    <button
      onClick={() => setTab(id)}
      data-testid={`tab-${id}`}
      className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-sm transition-colors ${tab === id ? "bg-y5-yellow text-y5-ink" : "text-y5-muted hover:text-white hover:bg-y5-surface"}`}
    >
      <span className="flex items-center gap-3">{icon}{label}</span>
      {typeof count === "number" && count > 0 && (
        <span className={`text-xs px-2 py-0.5 font-semibold ${tab === id ? "bg-y5-ink text-y5-yellow" : "bg-y5-yellow text-y5-ink"}`}>{count}</span>
      )}
    </button>
  );

  return (
    <div className="min-h-screen flex bg-y5-ink">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-y5-line p-6 fixed h-screen">
        <img src={LOGO} alt="Yellow5" className="h-8 mb-10" />
        <div className="text-y5-muted text-xs uppercase tracking-[0.2em] mb-3">Candidatures</div>
        <nav className="space-y-1 mb-8">
          {navItem("todo", "À traiter", <Inbox size={18} />, stats?.to_process_applications)}
          {navItem("done", "Traitées", <Check size={18} />, stats?.processed_applications)}
        </nav>
        <div className="text-y5-muted text-xs uppercase tracking-[0.2em] mb-3">Offres</div>
        <nav className="space-y-1 flex-1">
          {navItem("jobs", "Gérer les offres", <Briefcase size={18} />)}
        </nav>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="flex items-center gap-3 px-4 py-3 text-sm text-y5-muted hover:text-white"><LogOut size={18} /> Déconnexion</button>
      </aside>

      <main className="flex-1 md:ml-64 p-5 sm:p-6 lg:p-12 pb-20">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight uppercase">
              {tab === "todo" && "À traiter"}
              {tab === "done" && "Traitées"}
              {tab === "jobs" && "Offres"}
            </h1>
            <p className="text-y5-muted text-sm mt-1">Bienvenue, {user.name}</p>
          </div>
          {/* mobile tabs */}
          <div className="flex flex-wrap gap-2 md:hidden">
            <button onClick={() => setTab("todo")} className={`px-3 py-2 text-xs ${tab === "todo" ? "bg-y5-yellow text-y5-ink" : "border border-y5-line text-y5-muted"}`}>À traiter</button>
            <button onClick={() => setTab("done")} className={`px-3 py-2 text-xs ${tab === "done" ? "bg-y5-yellow text-y5-ink" : "border border-y5-line text-y5-muted"}`}>Traitées</button>
            <button onClick={() => setTab("jobs")} className={`px-3 py-2 text-xs ${tab === "jobs" ? "bg-y5-yellow text-y5-ink" : "border border-y5-line text-y5-muted"}`}>Offres</button>
          </div>
        </div>

        {/* Dashboard cards — always visible above content on todo tab, subtle strip elsewhere */}
        {tab === "todo" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            <button data-testid="stat-to-process" onClick={() => setTab("todo")} className="text-left border-2 border-y5-yellow bg-y5-yellow/5 p-8 hover:bg-y5-yellow/10 transition-colors">
              <div className="flex items-center justify-between mb-4"><Inbox className="text-y5-yellow" size={22} /><span className="text-y5-yellow text-xs uppercase tracking-widest">À traiter</span></div>
              <div className="font-display text-6xl tracking-tight text-y5-yellow">{stats?.to_process_applications ?? 0}</div>
              <div className="text-y5-muted text-xs mt-2">Candidatures en attente d&apos;examen</div>
            </button>
            <button onClick={() => setTab("done")} className="text-left border border-y5-line bg-y5-surface p-8 hover:bg-y5-surfaceHover transition-colors">
              <div className="flex items-center justify-between mb-4"><Check className="text-white/60" size={22} /><span className="text-y5-muted text-xs uppercase tracking-widest">Traitées</span></div>
              <div className="font-display text-6xl tracking-tight">{stats?.processed_applications ?? 0}</div>
              <div className="text-y5-muted text-xs mt-2">Candidatures déjà examinées</div>
            </button>
            <button onClick={() => setTab("jobs")} className="text-left border border-y5-line bg-y5-surface p-8 hover:bg-y5-surfaceHover transition-colors">
              <div className="flex items-center justify-between mb-4"><Briefcase className="text-white/60" size={22} /><span className="text-y5-muted text-xs uppercase tracking-widest">Offres actives</span></div>
              <div className="font-display text-6xl tracking-tight">{stats?.open_jobs ?? 0}</div>
              <div className="text-y5-muted text-xs mt-2">Offres publiées sur le site</div>
            </button>
          </div>
        )}

        {/* TODO tab */}
        {tab === "todo" && (
          <InboxList
            apps={toProcess}
            emptyLabel="Aucune candidature en attente. Boîte de réception vide."
            countLabel="Candidatures à traiter"
            onOpen={setSelected}
          />
        )}

        {/* DONE tab */}
        {tab === "done" && (
          <div>
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
              <div className="flex items-center gap-2 border border-y5-line bg-y5-surface px-4 flex-1">
                <Search size={16} className="text-y5-muted" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un candidat…" data-testid="search-input" className="flex-1 bg-transparent py-3 text-sm text-white placeholder:text-white/30 focus:outline-none" />
              </div>
              <select value={filterOffer} onChange={(e) => setFilterOffer(e.target.value)} className="bg-y5-surface border border-y5-line px-3 py-3 text-sm text-white focus:border-y5-yellow focus:outline-none" data-testid="filter-offer">
                <option value="">Toutes les offres</option>
                {offerOptions.map((o) => <option key={o} value={o} className="bg-y5-ink">{o}</option>)}
              </select>
              <select value={filterSector} onChange={(e) => setFilterSector(e.target.value)} className="bg-y5-surface border border-y5-line px-3 py-3 text-sm text-white focus:border-y5-yellow focus:outline-none" data-testid="filter-sector">
                <option value="">Tous les secteurs</option>
                {sectorOptions.map((s) => <option key={s} value={s} className="bg-y5-ink">{s}</option>)}
              </select>
            </div>
            <InboxList
              apps={processedFiltered}
              emptyLabel="Aucune candidature traitée pour le moment."
              countLabel="Candidatures traitées"
              showProcessedDate
              onOpen={setSelected}
            />
          </div>
        )}

        {/* JOBS tab */}
        {tab === "jobs" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <p className="text-y5-muted text-sm">{jobs.length} offre{jobs.length > 1 ? "s" : ""} au total</p>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <button onClick={openCreate} data-testid="new-job-btn" className="flex items-center gap-2 bg-y5-yellow text-y5-ink px-5 py-2.5 text-sm font-semibold hover:scale-[1.02] transition-transform"><Plus size={16} /> Nouvelle offre</button>
                </DialogTrigger>
                <DialogContent className="bg-y5-surface border-y5-line text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Modifier l'offre" : "Nouvelle offre"}</DialogTitle></DialogHeader>
                  <form onSubmit={saveJob} data-testid="job-form" className="space-y-4 mt-4">
                    <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Intitulé du poste (FR) *" required className={inputCls} data-testid="job-title" />
                    <input value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} placeholder="Job title (EN)" className={inputCls} data-testid="job-title-en" />
                    <div className="grid grid-cols-2 gap-4">
                      <select value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className={inputCls}>{SECTORS.map((s) => <option key={s} value={s} className="bg-y5-ink">{s}</option>)}</select>
                      <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Ville / Localisation *" required className={inputCls} data-testid="job-location" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <select value={form.province || ""} onChange={(e) => setForm({ ...form, province: e.target.value })} className={inputCls} data-testid="job-province" required>
                        <option value="" className="bg-y5-ink">— Province —</option>
                        {PROVINCES.map((p) => <option key={p} value={p} className="bg-y5-ink">{p}</option>)}
                      </select>
                      <select value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} className={inputCls}>{CONTRACTS.map((c) => <option key={c} value={c} className="bg-y5-ink">{c}</option>)}</select>
                    </div>
                    {editing && form.published_at && (
                      <div className="text-xs text-y5-muted flex items-center gap-2" data-testid="job-published-at">
                        <span className="uppercase tracking-widest text-y5-yellow">Publié le :</span>
                        {new Date(form.published_at).toLocaleString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    )}
                    <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description du poste (FR) *" rows={4} required className={inputCls} />
                    <textarea value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} placeholder="Job description (EN)" rows={4} className={inputCls} />
                    <textarea value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} placeholder="Profil recherché FR (une ligne par exigence)" rows={3} className={inputCls} />
                    <textarea value={form.requirements_en} onChange={(e) => setForm({ ...form, requirements_en: e.target.value })} placeholder="Requirements EN (one line per item)" rows={3} className={inputCls} />
                    <label className="flex items-center gap-3 text-sm text-y5-muted"><input type="checkbox" checked={form.is_open} onChange={(e) => setForm({ ...form, is_open: e.target.checked })} className="accent-y5-yellow w-4 h-4" /> Offre ouverte (visible publiquement)</label>
                    <button type="submit" data-testid="save-job-btn" className="w-full bg-y5-yellow text-y5-ink font-semibold py-3.5 hover:scale-[1.01] transition-transform">{editing ? "Enregistrer" : "Publier l'offre"}</button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="border border-y5-line divide-y divide-y5-line">
              {jobs.length === 0 && <div className="p-8 text-center text-y5-muted">Aucune offre. Créez votre première offre.</div>}
              {jobs.map((j) => (
                <div key={j.id} data-testid={`admin-job-${j.id}`} className="flex items-center justify-between p-5 hover:bg-y5-surface transition-colors">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <p className="text-white font-medium truncate">{j.title}</p>
                      <span className={`text-xs px-2 py-0.5 ${j.is_open ? "bg-y5-yellow/10 text-y5-yellow" : "bg-white/5 text-white/40"}`}>{j.is_open ? "Ouverte" : "Fermée"}</span>
                    </div>
                    <p className="text-y5-muted text-sm mt-1">{j.sector} · {j.location}{j.province ? ` · ${j.province}` : ""} · {j.contract_type}</p>
                    {(j.published_at || j.created_at) && (
                      <p className="text-y5-muted/70 text-xs mt-1">Publié le {new Date(j.published_at || j.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => openEdit(j)} data-testid={`edit-job-${j.id}`} className="p-2 border border-y5-line hover:border-y5-yellow hover:text-y5-yellow transition-colors"><Pencil size={16} /></button>
                    <button onClick={() => deleteJob(j.id)} data-testid={`delete-job-${j.id}`} className="p-2 border border-y5-line hover:border-red-500 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Application detail drawer */}
      {selected && (
        <ApplicationDrawer
          app={selected}
          onClose={() => setSelected(null)}
          onProcess={() => markProcessed(selected.id)}
          onUnprocess={() => markUnprocessed(selected.id)}
          onToggleAts={() => toggleAts(selected.id)}
          onDownloadCV={() => downloadCV(selected)}
        />
      )}
    </div>
  );
}

function InboxList({ apps, emptyLabel, countLabel, onOpen, showProcessedDate }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <span className="text-y5-muted text-sm">{countLabel} :</span>
        <span className="text-xs px-2 py-0.5 bg-y5-yellow text-y5-ink font-semibold">{apps.length}</span>
      </div>
      {apps.length === 0 ? (
        <div className="border border-y5-line p-10 text-center text-y5-muted">{emptyLabel}</div>
      ) : (
        <div className="border border-y5-line overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-y5-line text-y5-muted text-xs uppercase tracking-wider">
                <th className="text-left px-5 py-3 font-normal">Candidat</th>
                <th className="text-left px-5 py-3 font-normal">Offre</th>
                <th className="text-left px-5 py-3 font-normal hidden md:table-cell">Secteur</th>
                <th className="text-left px-5 py-3 font-normal hidden md:table-cell">Date</th>
                <th className="text-right px-5 py-3 font-normal">Action</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.id} data-testid={`admin-app-${a.id}`} className="border-b border-y5-line last:border-0 hover:bg-y5-surface transition-colors">
                  <td className="px-5 py-4 text-white font-medium">
                    <div>{a.name}</div>
                    <div className="text-y5-muted text-xs mt-0.5 md:hidden">{fmtDate(a.created_at)}</div>
                  </td>
                  <td className="px-5 py-4 text-white/80">{a.job_title || <span className="italic text-y5-muted">Candidature spontanée</span>}</td>
                  <td className="px-5 py-4 text-y5-muted hidden md:table-cell">{a.ai?.sector || "—"}</td>
                  <td className="px-5 py-4 text-y5-muted hidden md:table-cell">
                    {fmtDate(a.created_at)}
                    {showProcessedDate && a.processed_at && <div className="text-y5-muted/70 text-xs">Traité le {fmtDate(a.processed_at)}</div>}
                    {a.added_to_ats && <span className="ml-2 text-xs px-2 py-0.5 border border-y5-yellow/40 text-y5-yellow uppercase tracking-widest">ATS</span>}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => onOpen(a)} data-testid={`view-app-${a.id}`} className="inline-flex items-center gap-2 border border-y5-yellow text-y5-yellow px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-y5-yellow hover:text-y5-ink transition-colors">Voir <ExternalLink size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ApplicationDrawer({ app, onClose, onProcess, onUnprocess, onToggleAts, onDownloadCV }) {
  const processed = !!app.processed_at;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="app-detail">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-y5-surface sm:border-l border-y5-line h-full overflow-y-auto p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onClose} className="text-y5-muted hover:text-white text-sm flex items-center gap-2" data-testid="close-drawer"><ArrowLeft size={16} /> Retour</button>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X /></button>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <span className={`text-xs px-2 py-0.5 uppercase tracking-widest ${processed ? "bg-white/10 text-white/70" : "bg-y5-yellow text-y5-ink font-semibold"}`}>{processed ? "Traitée" : "À traiter"}</span>
          {app.added_to_ats && <span className="text-xs px-2 py-0.5 border border-y5-yellow/40 text-y5-yellow uppercase tracking-widest">Ajoutée à l&apos;ATS</span>}
        </div>
        <h3 className="font-display text-3xl mb-1 uppercase tracking-tight">{app.name}</h3>
        <p className="text-y5-muted mb-6">{app.job_title || "Candidature spontanée"}</p>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm mb-6">
          <div className="col-span-2"><dt className="text-y5-muted text-xs uppercase">Email</dt><dd className="text-white break-all">{app.email}</dd></div>
          {app.phone && <div><dt className="text-y5-muted text-xs uppercase">Téléphone</dt><dd className="text-white">{app.phone}</dd></div>}
          <div><dt className="text-y5-muted text-xs uppercase">Date</dt><dd className="text-white">{fmtDate(app.created_at)}</dd></div>
          {app.ai?.sector && <div><dt className="text-y5-muted text-xs uppercase">Secteur (IA)</dt><dd className="text-white">{app.ai.sector}</dd></div>}
          {typeof app.ai?.years_experience === "number" && <div><dt className="text-y5-muted text-xs uppercase">Expérience</dt><dd className="text-white">{app.ai.years_experience} ans</dd></div>}
        </dl>

        {app.message && (
          <div className="border border-y5-line p-4 mb-6 text-sm text-white/85">
            <p className="text-y5-muted text-xs uppercase mb-2">Message du candidat</p>
            {app.message}
          </div>
        )}

        <button
          onClick={onDownloadCV}
          data-testid="download-cv-btn"
          className="w-full flex items-center justify-center gap-2 border border-y5-yellow text-y5-yellow py-3 mb-6 hover:bg-y5-yellow hover:text-y5-ink transition-colors text-sm font-semibold"
        >
          <Download size={16} /> Télécharger le CV{app.cv_filename ? ` (${app.cv_filename})` : ""}
        </button>

        {(app.ai?.summary || (app.ai?.skills && app.ai.skills.length > 0)) && (
          <div className="border border-y5-line bg-y5-ink p-5 mb-6">
            <div className="flex items-center gap-2 text-y5-yellow mb-3"><Sparkles size={16} /><span className="text-xs uppercase tracking-widest">Analyse IA — usage interne</span></div>
            {app.ai?.summary && <p className="text-white/85 text-sm mb-4 leading-relaxed">{app.ai.summary}</p>}
            {app.ai?.skills?.length > 0 && (
              <div className="flex flex-wrap gap-2">{app.ai.skills.map((s, i) => <span key={i} className="text-xs border border-y5-line px-2 py-1 text-white/70">{s}</span>)}</div>
            )}
          </div>
        )}

        {/* Primary + secondary actions */}
        <div className="space-y-3">
          {processed ? (
            <button
              onClick={onUnprocess}
              data-testid="unprocess-btn"
              className="w-full flex items-center justify-center gap-2 border border-y5-line text-white py-4 hover:border-y5-yellow hover:text-y5-yellow transition-colors text-sm font-semibold"
            >
              <RotateCcw size={16} /> Restaurer dans « À traiter »
            </button>
          ) : (
            <button
              onClick={onProcess}
              data-testid="process-btn"
              className="w-full flex items-center justify-center gap-2 bg-y5-yellow text-y5-ink py-4 hover:scale-[1.01] transition-transform text-sm font-semibold uppercase tracking-widest"
            >
              <Check size={18} /> Marquer comme traitée
            </button>
          )}
          <button
            onClick={onToggleAts}
            data-testid="ats-btn"
            className="w-full flex items-center justify-center gap-2 border border-y5-line text-y5-muted py-3 hover:border-white/40 hover:text-white transition-colors text-xs uppercase tracking-widest"
          >
            {app.added_to_ats ? "Retirer de l'ATS" : "Ajouter à l'ATS"}
          </button>
        </div>
      </div>
    </div>
  );
}
